import React, { useState, useRef, useEffect } from "react";
import { useSelector } from 'react-redux';
import { useIntl } from "react-intl";
import moment from "moment";

import { getLocalTime } from "../../../../../utils/dateUtility";
import { shouldShowGroupSenderName } from "../../../../../utils/internalChatGroups";

import BeautyScrollbar from "../../../../beautyScrollbar/BeautyScrollbar";
import GoToBottom from "../../../../goToBottom/goToBottom";
import Loading from "../../../../loading/Loading";
import {
    Message,
    MessageSpacing,
    MessageText,
    MessageFooter,
    DividerDate
} from '../../../../messages/components';

import InternalAttachmentsViewer from "../../attachments/InternalAttachmentsViewer";
import InternalMessageAttachments from "../../attachments/InternalMessageAttachments";

import usePrevious from "../../../../../customHooks/usePrevious";
import useGroupWindow from "../../../utils/useGroupWindow";

import "./body.scss";

/** Corpo da janela do grupo. Reaproveita o balão da conversa individual, mas sem
 *  ícone de status (grupo não tem entrega/leitura) e sem apagar mensagem (a API de
 *  grupos não tem essa rota).
 */
const GroupBody = ({ group }) => {

    const intl = useIntl();
    const scrollRef = useRef();

    const loggedUserId = useSelector(state => state.auth.userData?.id);
    const { loadingMessages, loadingOlderMessages, loadOlderMessages } = useGroupWindow(group.id);

    const [allowScroll, setAllowScroll] = useState(true);
    const [attachmentsViewer, setAttachmentsViewer] = useState(null);

    /** O balão decide enviada/recebida por `isSent` e agrupa a sequência por
     *  `userSender`, campos que a mensagem de grupo não tem.
     */
    const messages = React.useMemo(() => {
        return (group.messages || []).map(message => {
            const isSent = message.userFromId === loggedUserId;
            return {
                ...message,
                isSent,
                userSender: isSent ? { id: message.userFromId, name: message.userFromName } : null
            };
        });
    }, [group.messages, loggedUserId]);

    const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : null;
    const previousLastMessageId = usePrevious(lastMessageId);

    const onWheelScroll = (e) => {
        const { bottom } = e.contentInset
        setAllowScroll(bottom < 20)
    }

    const handleScrollToBottom = () => {
        scrollRef.current.scrollToEnd({ behavior: "auto" });
        setAllowScroll(true)
    }

    const handleLoadOlderMessages = (e) => {
        const { bottom } = e.contentInset
        if (bottom > 0) loadOlderMessages()
    }

    /** Ao trocar de grupo, a leitura recomeça pelo fim. */
    useEffect(() => {
        setAllowScroll(true)
    }, [group.id])

    /** Rola até o fim quando chega a primeira página ou uma mensagem nova no fim.
     *  Carregar as antigas muda só o começo da lista, e o BeautyScrollbar mantém o
     *  usuário no mesmo ponto de leitura.
     */
    useEffect(() => {
        if (lastMessageId && lastMessageId !== previousLastMessageId && allowScroll && scrollRef.current) {
            scrollRef.current.scrollToEnd({ behavior: "auto" })
        }
    }, [lastMessageId])

    return (
        <>
            <div className="chat-window-body-ui">
                <BeautyScrollbar
                    onWheelScroll={onWheelScroll}
                    ref={scrollRef}
                    keepHeightScrollPosition
                    onReachVerticalStart={handleLoadOlderMessages}
                    className="chat-window-body-messages">
                    {(loadingMessages || loadingOlderMessages) && <Loading />}
                    {!loadingMessages && messages.length === 0 &&
                        <div className="chat-window-body-empty">
                            {intl.formatMessage({ id: "Nenhuma mensagem no grupo ainda" })}
                        </div>
                    }
                    {messages.map((message, index) => {
                        const prevMessage = messages[index - 1] ?? null;
                        const nextMessage = messages[index + 1] ?? null;
                        return (
                            <React.Fragment key={message.id}>
                                <DividerDate
                                    currDate={message.createdAt}
                                    prevDate={prevMessage?.createdAt}
                                />
                                <Message
                                    item={message}
                                    prevMessage={prevMessage}
                                    nextMessage={nextMessage}
                                    isChatbot={false}
                                    isSent={message.isSent}
                                    color={message.isSent ? "success" : "light"}
                                    style={{
                                        maxWidth: "100%"
                                    }}
                                >
                                    {shouldShowGroupSenderName(message, prevMessage, loggedUserId) &&
                                        <small className="chat-window-message-sender">{message.userFromName}</small>
                                    }
                                    <InternalMessageAttachments
                                        isGroup
                                        messageId={message.id}
                                        attachments={message.attachments}
                                        onOpenViewer={(attachmentIndex) => setAttachmentsViewer({
                                            attachments: message.attachments,
                                            messageId: message.id,
                                            index: attachmentIndex,
                                            isGroup: true
                                        })}
                                    />
                                    {/* MessageText faz `children.trim()`: mensagem só com anexo vem sem texto. */}
                                    {message.messageText && <MessageText>{message.messageText}</MessageText>}
                                    <MessageFooter>
                                        <span>{moment(getLocalTime(message?.createdAt)).format('HH:mm')}</span>
                                    </MessageFooter>
                                </Message>
                                {index + 1 < messages.length && <MessageSpacing nextMessage={nextMessage} currMessage={message} />}
                            </React.Fragment>
                        )
                    })}
                </BeautyScrollbar>
                {!allowScroll && <GoToBottom onClick={handleScrollToBottom} />}
            </div>
            <InternalAttachmentsViewer
                viewer={attachmentsViewer}
                onClose={() => setAttachmentsViewer(null)}
            />
        </>
    )
};

export default GroupBody;
